import type { ModuleCode, PlatformOwnerInvitationResponse } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  Notice,
  PageHeader,
  Skeleton,
  Switch,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Ban,
  Blocks,
  Building2,
  CreditCard,
  Pencil,
  RotateCcw,
  ShieldCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { subscriptionStatusTone, tenantStatusTone } from '@/features/platform/labels';
import {
  platformKeys,
  platformTenantQueryOptions,
  reactivatePlatformTenant,
  setPlatformModule,
} from '@/features/platform/queries';
import {
  OwnerInvitationDrawer,
  SubscriptionDialog,
  SuspendDialog,
  TenantEditDrawer,
} from '@/features/platform/TenantDialogs';
import { BillingCard } from '@/features/subscription-billing/BillingCard';
import { SupportAccessCard } from '@/features/support/SupportAccessCard';
import { DeliveryLinkDialog } from '@/features/users/DeliveryLinkDialog';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateShort, formatDateTime, formatRelative } from '@/lib/format';

export const Route = createFileRoute('/_app/platform/tenants/$tenantId')({
  component: PlatformTenantPage,
});

/** Fiche d'un tenant (ADR-021) : fiche, abonnement, modules, établissements, Owner, cycle de vie. */
function PlatformTenantPage() {
  const { t, i18n } = useTranslation(['platform', 'common', 'errors', 'settings', 'users']);
  const { tenantId } = Route.useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canManage = session.can('platform.manage_tenants');
  const query = useQuery(platformTenantQueryOptions(tenantId));
  const [editOpen, setEditOpen] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invited, setInvited] = useState<PlatformOwnerInvitationResponse | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: platformKeys.all });

  const toggleModule = useMutation({
    mutationFn: (input: { code: ModuleCode; enabled: boolean }) =>
      setPlatformModule(tenantId, input.code, input.enabled),
    onSuccess: async (_tenant, input) => {
      toast.success(
        t(input.enabled ? 'platform:toasts.moduleEnabled' : 'platform:toasts.moduleDisabled'),
      );
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const reactivate = useMutation({
    mutationFn: () => reactivatePlatformTenant(tenantId),
    onSuccess: async () => {
      toast.success(t('platform:toasts.reactivated'));
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={160} />
          <Skeleton height={260} />
        </div>
      </Container>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const tenant = query.data;
  const name = tenant.tradeName ?? tenant.legalName;
  const subscription = tenant.subscription;
  const suspended = tenant.status === 'SUSPENDED';

  return (
    <Container>
      <PageHeader
        title={name}
        subtitle={`${tenant.legalName} · ${tenant.slug}`}
        breadcrumb={[
          { label: t('platform:title'), link: <Link to="/platform">{t('platform:title')}</Link> },
          {
            label: t('platform:tenants.title'),
            link: <Link to="/platform/tenants">{t('platform:tenants.title')}</Link>,
          },
          { label: name },
        ]}
        actions={
          canManage ? (
            <div className="row row--wrap" style={{ gap: 8 }}>
              <Button variant="secondary" onClick={() => setEditOpen(true)}>
                <Pencil className="ico" aria-hidden="true" />
                {t('common:actions.edit')}
              </Button>
              {suspended ? (
                <Button
                  variant="success"
                  disabled={reactivate.isPending}
                  onClick={() => reactivate.mutate()}
                >
                  <RotateCcw className="ico" aria-hidden="true" />
                  {t('platform:detail.reactivate')}
                </Button>
              ) : (
                <Button variant="danger" onClick={() => setSuspendOpen(true)}>
                  <Ban className="ico" aria-hidden="true" />
                  {t('platform:detail.suspend')}
                </Button>
              )}
            </div>
          ) : null
        }
      />

      {suspended ? <Notice tone="danger">{t('platform:detail.suspendedNotice')}</Notice> : null}

      <div className="stack">
        <Card>
          <CardHeader
            title={t('platform:detail.identity')}
            icon={<Building2 className="ico ico-16" aria-hidden="true" />}
            actions={
              <Badge tone={tenantStatusTone(tenant.status)}>
                {t(`platform:status.${tenant.status}`)}
              </Badge>
            }
          />
          <CardBody>
            <dl className="grid-4 dl-compact">
              <div>
                <dt className="t-12 muted">{t('platform:form.countryCode')}</dt>
                <dd>{tenant.countryCode}</dd>
              </div>
              <div>
                <dt className="t-12 muted">{t('platform:form.currencyCode')}</dt>
                <dd>{tenant.currencyCode}</dd>
              </div>
              <div>
                <dt className="t-12 muted">{t('platform:form.timezone')}</dt>
                <dd>{tenant.timezone}</dd>
              </div>
              <div>
                <dt className="t-12 muted">{t('platform:form.defaultLocale')}</dt>
                <dd>{t(`platform:locales.${tenant.defaultLocale}`)}</dd>
              </div>
              <div>
                <dt className="t-12 muted">{t('platform:tenants.columns.created')}</dt>
                <dd>{formatDateTime(tenant.createdAt, i18n.language)}</dd>
              </div>
              <div>
                <dt className="t-12 muted">{t('platform:tenants.columns.lastLogin')}</dt>
                <dd>
                  {tenant.lastLoginAt
                    ? formatRelative(tenant.lastLoginAt, i18n.language)
                    : t('platform:tenants.neverLoggedIn')}
                </dd>
              </div>
              <div>
                <dt className="t-12 muted">{t('platform:tenants.columns.properties')}</dt>
                <dd>{tenant.propertiesCount}</dd>
              </div>
              <div>
                <dt className="t-12 muted">{t('platform:tenants.columns.users')}</dt>
                <dd>{tenant.usersCount}</dd>
              </div>
            </dl>
          </CardBody>
        </Card>

        <div className="grid-2">
          <Card>
            <CardHeader
              title={t('platform:subscription.title')}
              icon={<CreditCard className="ico ico-16" aria-hidden="true" />}
              actions={
                canManage && !suspended ? (
                  <Button size="sm" variant="secondary" onClick={() => setSubscriptionOpen(true)}>
                    {t('platform:subscription.edit')}
                  </Button>
                ) : null
              }
            />
            <CardBody>
              {subscription ? (
                <div className="stack" style={{ gap: 8 }}>
                  <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                    <strong>{subscription.planName}</strong>
                    <Badge tone={subscriptionStatusTone(subscription.status)}>
                      {t(`platform:subscriptionStatus.${subscription.status}`)}
                    </Badge>
                  </div>
                  <p className="t-12 muted">
                    {t('platform:subscription.since', {
                      date: formatDateShort(subscription.startsAt, i18n.language),
                    })}
                    {subscription.trialEndsAt
                      ? ` · ${t('platform:subscription.trialEnds', {
                          date: formatDateShort(subscription.trialEndsAt, i18n.language),
                        })}`
                      : ''}
                    {subscription.endsAt
                      ? ` · ${t('platform:subscription.ends', {
                          date: formatDateShort(subscription.endsAt, i18n.language),
                        })}`
                      : ''}
                  </p>
                  {subscription.notes ? <p className="small">{subscription.notes}</p> : null}
                </div>
              ) : (
                <Notice tone="warning">{t('platform:subscription.none')}</Notice>
              )}
              {tenant.subscriptions.length > 1 ? (
                <details style={{ marginTop: 12 }}>
                  <summary className="t-12 muted">{t('platform:subscription.history')}</summary>
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('platform:form.plan')}</Th>
                          <Th>{t('platform:form.status')}</Th>
                          <Th>{t('platform:subscription.startsAt')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenant.subscriptions.map((row) => (
                          <Tr key={row.id}>
                            <Td>{row.planName}</Td>
                            <Td>{t(`platform:subscriptionStatus.${row.status}`)}</Td>
                            <Td>{formatDateShort(row.startsAt, i18n.language)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                </details>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('platform:modules.title')}
              icon={<Blocks className="ico ico-16" aria-hidden="true" />}
            />
            <CardBody>
              <ul className="list-plain" style={{ display: 'grid', gap: 8 }}>
                {tenant.moduleCatalog.map((module) => {
                  const togglable =
                    canManage &&
                    !suspended &&
                    Boolean(subscription) &&
                    module.status === 'AVAILABLE';
                  const label = module.enabled
                    ? module.source === 'ADDON'
                      ? t('platform:modules.addon')
                      : t('platform:modules.plan')
                    : module.status === 'COMING_SOON'
                      ? t('platform:moduleStatus.COMING_SOON')
                      : module.inPlan
                        ? t('platform:modules.inPlanDisabled')
                        : t('platform:modules.off');
                  return (
                    <li
                      key={module.code}
                      className="row"
                      style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
                      data-testid={`platform-module-${module.code}`}
                    >
                      <span className="stack" style={{ gap: 2 }}>
                        <span>{module.name}</span>
                        <span className="t-12 muted">
                          {label}
                          {module.dependsOn.length > 0
                            ? ` · ${t('settings:modules.dependsOn', {
                                modules: module.dependsOn
                                  .map((code) => t(`settings:modules.names.${code}`))
                                  .join(', '),
                              })}`
                            : ''}
                        </span>
                      </span>
                      <Switch
                        checked={module.enabled}
                        disabled={!togglable || toggleModule.isPending}
                        aria-label={t('platform:modules.toggle', { module: module.name })}
                        onCheckedChange={(checked) =>
                          toggleModule.mutate({ code: module.code, enabled: checked })
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            </CardBody>
          </Card>
        </div>

        <div className="grid-2">
          <Card>
            <CardHeader
              title={t('platform:detail.properties')}
              icon={<Building2 className="ico ico-16" aria-hidden="true" />}
            />
            <CardBody density="flush">
              {tenant.properties.length === 0 ? (
                <EmptyState title={t('platform:detail.noProperty')} />
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('platform:form.propertyCode')}</Th>
                        <Th>{t('platform:form.propertyName')}</Th>
                        <Th>{t('platform:form.activities')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenant.properties.map((property) => (
                        <Tr key={property.id}>
                          <Td>
                            <code>{property.code}</code>
                          </Td>
                          <Td>
                            {property.name}
                            {!property.isActive ? (
                              <Badge tone="neutral" style={{ marginLeft: 6 }}>
                                {t('platform:detail.inactive')}
                              </Badge>
                            ) : null}
                          </Td>
                          <Td>
                            {property.activities
                              .map((activity) => t(`platform:activities.${activity}`))
                              .join(', ')}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('platform:owner.title')}
              icon={<Users className="ico ico-16" aria-hidden="true" />}
              actions={
                canManage ? (
                  <Button size="sm" variant="secondary" onClick={() => setInviteOpen(true)}>
                    <UserPlus className="ico ico-16" aria-hidden="true" />
                    {t('platform:owner.invite')}
                  </Button>
                ) : null
              }
            />
            <CardBody density="flush">
              {tenant.owners.length === 0 ? (
                <EmptyState title={t('platform:owner.none')} />
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('platform:owner.columns.account')}</Th>
                        <Th>{t('platform:owner.columns.status')}</Th>
                        <Th>{t('platform:owner.columns.lastLogin')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenant.owners.map((owner) => (
                        <Tr key={owner.id}>
                          <Td>
                            <div className="stack" style={{ gap: 2 }}>
                              <strong>{owner.fullName}</strong>
                              <span className="t-12 muted">{owner.email}</span>
                            </div>
                          </Td>
                          <Td>
                            <span className="row row--wrap" style={{ gap: 4 }}>
                              <Badge
                                tone={
                                  owner.status === 'ACTIVE'
                                    ? 'green'
                                    : owner.status === 'INVITED'
                                      ? 'amber'
                                      : 'red'
                                }
                              >
                                {t(`users:status.${owner.status}`)}
                              </Badge>
                              {owner.invitationPending ? (
                                <Badge tone="blue">{t('platform:owner.invitationPending')}</Badge>
                              ) : null}
                              {owner.mfaEnabled ? (
                                <Badge tone="green">
                                  <ShieldCheck className="ico ico-16" aria-hidden="true" />
                                  {t('platform:owner.mfa')}
                                </Badge>
                              ) : null}
                            </span>
                          </Td>
                          <Td>
                            {owner.lastLoginAt
                              ? formatRelative(owner.lastLoginAt, i18n.language)
                              : t('platform:tenants.neverLoggedIn')}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <BillingCard tenant={tenant} />

      {!suspended ? <SupportAccessCard tenantId={tenant.id} tenantName={name} /> : null}

      {canManage ? (
        <>
          <TenantEditDrawer tenant={tenant} open={editOpen} onOpenChange={setEditOpen} />
          <SubscriptionDialog
            tenant={tenant}
            open={subscriptionOpen}
            onOpenChange={setSubscriptionOpen}
          />
          <SuspendDialog tenant={tenant} open={suspendOpen} onOpenChange={setSuspendOpen} />
          <OwnerInvitationDrawer
            tenant={tenant}
            open={inviteOpen}
            onOpenChange={setInviteOpen}
            onInvited={(result) => setInvited(result)}
          />
        </>
      ) : null}

      <DeliveryLinkDialog
        link={invited?.activation ?? null}
        recipient={invited ? { fullName: invited.fullName, email: invited.email } : null}
        onClose={() => setInvited(null)}
      />
    </Container>
  );
}
